import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { guacHttpClient } from "@/lib/http";
import {
  HttpResponse,
  SadieSimulatorDefaults,
  SadieSimulatorPostPipeline,
  SadieSimulatorSessionKind,
  SadieSimulatorWorkerInitiated,
} from "@/types";

export function useGetSadieSimulatorDefaults(
  personaKey?: string,
  sessionKind?: SadieSimulatorSessionKind,
  matchedOpportunityKey?: string,
  openerInstructions?: string,
  handoff?: { digest: string; intentLevel: string | null },
  workerInitiated?: SadieSimulatorWorkerInitiated,
  postPipeline?: SadieSimulatorPostPipeline,
) {
  return useQuery({
    queryKey: [
      "sadie-simulator-defaults",
      personaKey ?? null,
      sessionKind ?? null,
      matchedOpportunityKey ?? null,
      openerInstructions ?? null,
      handoff ?? null,
      workerInitiated ?? null,
      postPipeline ?? null,
    ],
    queryFn: async () => {
      // Sessions 3 and 4 describe a search the same way.
      const jobSearch = workerInitiated?.jobSearch ?? postPipeline?.jobSearch;
      const res = await guacHttpClient.get<
        SadieSimulatorDefaults,
        HttpResponse<SadieSimulatorDefaults>
      >("relationships/simulate/defaults", {
        params: {
          ...(personaKey ? { persona: personaKey } : undefined),
          ...(sessionKind ? { sessionKind } : undefined),
          ...(matchedOpportunityKey
            ? { matchedOpportunity: matchedOpportunityKey }
            : undefined),
          ...(openerInstructions ? { openerInstructions } : undefined),
          ...(handoff ? { digest: handoff.digest } : undefined),
          ...(handoff?.intentLevel
            ? { intentLevel: handoff.intentLevel }
            : undefined),
          ...(workerInitiated?.pipeline
            ? { pipeline: workerInitiated.pipeline }
            : undefined),
          ...(workerInitiated?.session1Incomplete
            ? { session1Incomplete: true }
            : undefined),
          ...(postPipeline?.checkIn
            ? { checkIn: postPipeline.checkIn }
            : undefined),
          ...(jobSearch ? { jobSearchStatus: jobSearch.status } : undefined),
          ...(jobSearch?.status === "FOUND"
            ? {
                jobSearchOpportunity: jobSearch.opportunityKey,
                ...(jobSearch.previouslyShown
                  ? { jobSearchPreviouslyShown: true }
                  : undefined),
                ...(jobSearch.previouslyShown && jobSearch.priorReaction
                  ? { jobSearchPriorReaction: jobSearch.priorReaction }
                  : undefined),
              }
            : undefined),
        },
      });
      return res.data;
    },
    // The prompt is rendered from code and does not change under the page.
    staleTime: Infinity,
    // A worker switch swaps the data in place instead of unmounting the page.
    placeholderData: keepPreviousData,
  });
}
